import { For, Show, createSignal } from "solid-js";
import { api } from "../api/client";
import type { Exercise, WorkoutTemplate } from "../api/types";
import ExerciseEditor from "../components/ExerciseEditor";
import PageHero from "../components/PageHero";
import Cell from "../components/tg/Cell";
import Section from "../components/tg/Section";
import { t } from "../i18n";
import { rowIssue } from "../lib/activity";
import { createCachedResource } from "../lib/cache";
import { useCatalog } from "../lib/data";
import { newExercise, resolveRowsFromCatalog } from "../lib/exerciseRows";
import { cloneExercises } from "../lib/repeatWorkout";
import { withSortOrder } from "../lib/reorder";
import { tgConfirm } from "../lib/telegram";

export default function TemplatesPage() {
  const [templates, { refetch }] = createCachedResource(
    () => true,
    { key: () => "templates", fetch: () => api.listTemplates() },
  );
  const [editing, setEditing] = createSignal<WorkoutTemplate | null>(null);
  const [creating, setCreating] = createSignal(false);
  const [name, setName] = createSignal("");
  const [exercises, setExercises] = createSignal<Exercise[]>([newExercise()]);
  const [showErrors, setShowErrors] = createSignal(false);
  const [catalog] = useCatalog();
  const [saving, setSaving] = createSignal(false);

  function openCreate() {
    setCreating(true);
    setEditing(null);
    setName("");
    setExercises([newExercise()]);
    setShowErrors(false);
  }

  function openEdit(tpl: WorkoutTemplate) {
    setEditing(tpl);
    setCreating(false);
    setName(tpl.name);
    setExercises(cloneExercises(tpl.exercises, { copyNotes: true }));
    setShowErrors(false);
  }

  function closeForm() {
    setCreating(false);
    setEditing(null);
  }

  async function save() {
    const resolved = resolveRowsFromCatalog(exercises(), catalog() ?? []);
    setExercises(resolved);
    const rows = resolved.filter((ex) => ex.name.trim());
    if (rows.some((ex) => rowIssue(ex))) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name().trim(),
        exercises: withSortOrder(rows),
      };
      if (editing()) {
        await api.updateTemplate(editing()!.id, body);
      } else {
        await api.createTemplate(body);
      }
      closeForm();
      await refetch();
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!(await tgConfirm(t("CONFIRM_DELETE")))) return;
    await api.deleteTemplate(id);
    await refetch();
  }

  return (
    <div>
      <PageHero
        title={t("TEMPLATES")}
        aside={
          <button type="button" class="btn-ghost px-2" onClick={openCreate}>
            {t("CREATE")}
          </button>
        }
      />

      <Show when={creating() || editing()}>
        <Section header={editing() ? t("EDIT") : t("CREATE")}>
          <div class="px-4 py-3 space-y-3">
            <div>
              <label class="label">{t("TITLE")}</label>
              <input
                class="input"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </div>
            <ExerciseEditor
              exercises={exercises()}
              onChange={setExercises}
              showErrors={showErrors()}
            />
          </div>
        </Section>
        <div class="mb-4 flex gap-2">
          <button type="button" class="btn-secondary flex-1" onClick={closeForm}>
            {t("CANCEL")}
          </button>
          <button
            type="button"
            class="btn-primary flex-1"
            disabled={saving()}
            onClick={save}
          >
            {t("SAVE")}
          </button>
        </div>
        <Show when={editing()}>
          <Section>
            <Cell
              title={t("DELETE")}
              destructive
              onClick={() => void remove(editing()!.id)}
            />
          </Section>
        </Show>
      </Show>

      <Show when={templates.loading}>
        <p class="text-center text-[15px] text-ink-muted">{t("LOADING")}</p>
      </Show>

      <Show
        when={!templates.loading && (templates()?.length ?? 0) === 0 && !creating()}
      >
        <div class="empty-state">
          <p class="empty-state-title">{t("NO_TEMPLATES")}</p>
        </div>
      </Show>

      <Show when={(templates()?.length ?? 0) > 0}>
        <Section>
          <For each={templates()}>
            {(tpl) => (
              <Cell
                title={tpl.name}
                subtitle={tpl.exercises.map((e) => e.name).join(" · ")}
                chevron
                onClick={() => openEdit(tpl)}
              />
            )}
          </For>
        </Section>
      </Show>
    </div>
  );
}
